import React, { useEffect, useState } from "react";
import { useEditFromPrompt } from "@/hooks/useLocalStorage";
import { useBreadcrumbs } from "./BreadcrumbContext";
import { useToast } from "@/hooks/use-toast";
import {
  Sparkles,
  Play,
  CheckCircle,
  AlertTriangle,
  Info,
  ArrowRight,
  Bot,
  RefreshCw,
  Sliders,
  Calendar,
  Layers,
  ArrowRightLeft,
  ChevronRight,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

interface RuleItem {
  ID: number;
  Name: string;
  Body: string;
  Description: string;
  ApplicationID?: number;
  Type?: number;
  CreatedBy?: string;
  ModifiedBy?: string;
  ModifiedDate?: string;
  CreatedDate?: string;
  Status?: number;
}

interface EditFromPromptResponse {
  id?: number | null;
  name?: string | null;
  body?: string | null;
  previous_name?: string | null;
  previous_body?: string | null;
  changes?: any[];
  dry_run?: boolean;
  persisted?: boolean;
  message?: string;
  raw_rule_json?: any;
  action?: string;
  rules?: RuleItem[];
}

const PRESET_PROMPTS = [
  {
    label: "List rules with name like Insurance",
    text: "List rules with name like Insurance, page 1, page size 10."
  },
  {
    label: "Edit rule 45 to creditRule",
    text: "Change the name of rule with ID 45 to creditRule."
  },
  {
    label: "Find rule with ID 33",
    text: "Find rule with ID 33 and show detail."
  },
  {
    label: "Set rule 33 status to active",
    text: "Set the status of rule with ID 33 to Active."
  }
];

export function AiNlps() {
  const [prompt, setPrompt] = useState("");
  const [dryRun, setDryRun] = useState(false);
  const [result, setResult] = useState<EditFromPromptResponse | null>(null);
  const { setBreadcrumbs } = useBreadcrumbs();
  const { toast } = useToast();

  const editMutation = useEditFromPrompt();

  useEffect(() => {
    setBreadcrumbs([
      {
        label: "AI / NLPS",
        path: "/ai-nlps",
      },
    ]);
  }, [setBreadcrumbs]);

  const handleSend = async () => {
    if (!prompt.trim()) {
      toast({
        title: "Prompt is empty",
        description: "Please describe what you want the AI agent to do.",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await editMutation.mutateAsync({
        prompt: prompt.trim(),
        dry_run: dryRun,
      });

      setResult(response);
      toast({
        title: "Agent Execution Success",
        description: "The AI agent completed the action successfully.",
      });
    } catch (error: any) {
      console.error("AI execution error:", error);
      toast({
        title: "Execution Failed",
        description: error.message || "An error occurred during prompt processing.",
        variant: "destructive",
      });
    }
  };

  const handlePresetClick = (text: string) => {
    setPrompt(text);
  };

  const formatMessageText = (text?: string) => {
    if (!text) return null;

    // A helper to format simple markdown-like elements (bold headers, bullet points)
    const lines = text.split("\n");
    return lines.map((line, idx) => {
      let trimmed = line.trim();

      // Match markdown headers e.g. ### Header
      if (trimmed.startsWith("###")) {
        return (
          <h4 key={idx} className="text-md font-semibold text-gray-800 mt-3 mb-1">
            {trimmed.replace(/^###\s*/, "")}
          </h4>
        );
      }

      // Match markdown bold e.g. 1. **Name**: Insurance Expiration
      // Replace **text** with strong elements
      const boldRegex = /\*\*(.*?)\*\*/g;
      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = boldRegex.exec(line)) !== null) {
        if (match.index > lastIndex) {
          parts.push(line.substring(lastIndex, match.index));
        }
        parts.push(<strong key={match.index} className="font-semibold text-gray-900">{match[1]}</strong>);
        lastIndex = boldRegex.lastIndex;
      }

      if (lastIndex < line.length) {
        parts.push(line.substring(lastIndex));
      }

      // Check if it looks like a list item
      const isListItem = trimmed.startsWith("-") || trimmed.startsWith("*") || /^\d+\./.test(trimmed);

      return (
        <p key={idx} className={`text-gray-700 leading-relaxed ${isListItem ? "ml-4 pl-2 border-l-2 border-workfinity-200 my-1" : "my-1.5"}`}>
          {parts.length > 0 ? parts : line}
        </p>
      );
    });
  };

  return (
    <div className="min-h-full p-2 md:p-2 space-y-6">
      {/* Title section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-gray-200 pb-5">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            {/* <Sparkles className="h-6 w-6 text-workfinity-500 animate-pulse" /> */}
            AI / NLPS Agent
          </h2>
          <p className="text-gray-900 mt-1.5 text-sm md:text-base max-w-3xl">
            Natural language processing for data operations. The agent executes your task directly (e.g. searching, updates, name changes) and provides a summary of results.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Input Section */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-gray-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Sliders className="h-5 w-5 text-gray-500" />
                Configure Command
              </CardTitle>
              <CardDescription>
                Describe the operation you would like to run on the rules base.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Preset prompt chips */}
              {/* <div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">
                  Preset Templates
                </span>
                <div className="flex flex-wrap gap-2">
                  {PRESET_PROMPTS.map((preset, idx) => (
                    <button
                      key={idx}
                      onClick={() => handlePresetClick(preset.text)}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:border-workfinity-300 hover:bg-workfinity-50 rounded-full transition-all duration-200 cursor-pointer shadow-2xs"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div> */}

              {/* Textarea */}
              <div className="space-y-2">
                <Label htmlFor="prompt-input" className="text-sm font-medium text-gray-700">
                  Natural Language Prompt
                </Label>
                <Textarea
                  id="prompt-input"
                  placeholder="e.g., Change the name of rule with ID 45 to creditRule."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  maxLength={500}
                  className="min-h-[120px] focus:ring-workfinity-500 border-gray-300 rounded-lg text-sm md:text-base leading-relaxed bg-white shadow-2xs"
                />
                <div className="flex justify-between items-center text-xs text-gray-900">
                  <span>Be descriptive (mention Rule names, IDs, or search patterns).</span>
                  <span className={prompt.length >= 500 ? "text-red-500 font-bold" : ""}>
                    {prompt.length} / 500 characters
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Controls Section */}
        <div className="space-y-6">
          <Card className="border-gray-200 shadow-sm h-full flex flex-col justify-between">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                Execution Controls
              </CardTitle>
              <CardDescription>
                Customize how the prompt is evaluated.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6 flex-grow">

              {/* Status information info box */}
              {/* <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 flex gap-2.5">
                <Info className="h-4.5 w-4.5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold mb-0.5">Execution Flow</p>
                  <p className="leading-relaxed">
                    This agent is procedural, not conversational. It translates prompts into database changes, executes them immediately, and lists the results below.
                  </p>
                </div>
              </div> */}
              {/* Dry Run Toggle Switch */}
              <div className="flex items-start justify-between space-x-4 p-4 bg-white border border-gray-200 rounded-lg shadow-2xs">
                <div className="space-y-1">
                  <Label htmlFor="dry-run-switch" className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                    Dry Run Mode
                  </Label>
                  <p className="text-xs text-gray-900">
                    Preview outcome and query matches without altering database records.
                  </p>
                </div>
                <Switch
                  id="dry-run-switch"
                  checked={dryRun}
                  onCheckedChange={setDryRun}
                  className="data-[state=checked]:bg-workfinity-600 data-[state=unchecked]:bg-gray-300"
                />
              </div>
            </CardContent>

            <div className="p-6 pt-0 mt-auto border-t border-gray-100 bg-gray-50/50 rounded-b-lg">
              <Button
                onClick={handleSend}
                disabled={editMutation.isPending}
                className="w-full bg-workfinity-600 hover:bg-workfinity-700 text-white font-medium py-5 rounded-lg flex items-center justify-center gap-2 transition shadow-md hover:shadow-lg disabled:opacity-75 cursor-pointer mt-4"
              >
                {editMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Executing Command...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 fill-white" />
                    Send Prompt
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Results Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          Execution Results
        </h3>

        {editMutation.isPending && (
          <Card className="border-gray-200 shadow-sm animate-pulse">
            <CardContent className="py-12 flex flex-col items-center justify-center text-center space-y-4">
              <RefreshCw className="h-10 w-10 text-workfinity-500 animate-spin" />
              <div className="space-y-1">
                <h4 className="font-semibold text-gray-800">Processing Request...</h4>
                <p className="text-sm text-gray-900">The rules agent is analyzing and executing your command.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {!editMutation.isPending && !result && (
          <Card className="border-gray-200 border-dashed bg-white shadow-xs">
            <CardContent className="py-16 flex flex-col items-center justify-center text-center space-y-4">
              <div className="p-4 bg-gray-100 rounded-full text-gray-400">
                <Bot className="h-10 w-10" />
              </div>
              <div className="space-y-1 max-w-sm">
                <h4 className="font-semibold text-gray-800">No Action Taken Yet</h4>
                <p className="text-sm text-gray-500">
                  Input a prompt above and click "Send Prompt" to see modifications or list rule details.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {!editMutation.isPending && result && (
          <div className="space-y-6">
            {/* Overview Banner */}
            <Card className={`border shadow-sm overflow-hidden ${result.dry_run ? "border-amber-200 bg-amber-50/20" : "border-emerald-200 bg-emerald-50/20"
              }`}>
              <div className="p-4 flex flex-wrap gap-4 items-center justify-between border-b border-gray-100 bg-white">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${result.dry_run ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                    }`}>
                    {result.dry_run ? <Info className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-gray-900">
                        {result.dry_run ? "Dry Run Completed" : "Action Executed"}
                      </h4>
                      <Badge className={result.dry_run ? "bg-amber-100 text-amber-800 hover:bg-amber-100" : "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"}>
                        {result.dry_run ? "DRY RUN" : "PERSISTED"}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500">
                      Operation type: <span className="font-mono bg-gray-100 px-1 py-0.5 rounded text-gray-700 font-semibold">{result.action || "unknown"}</span>
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  {result.id && (
                    <Badge variant="outline" className="bg-gray-50 text-gray-700 font-mono py-1 px-2 border-gray-200">
                      Rule ID: {result.id}
                    </Badge>
                  )}
                  {result.persisted !== undefined && (
                    <Badge className={result.persisted ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-800"}>
                      {result.persisted ? "DB Updated" : "No DB Write"}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Message Display */}
              <CardContent className="p-4 md:p-6">
                <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6 shadow-2xs">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-3">
                    Agent Log Message
                  </span>
                  <div className="space-y-1">
                    {formatMessageText(result.message)}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Visual Differences (Diff) Viewer */}
            {(result.previous_name || result.previous_body || (result.changes && result.changes.length > 0)) && (
              <Card className="border-gray-200 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <ArrowRightLeft className="h-5 w-5 text-workfinity-500" />
                    Rule Modification Details
                  </CardTitle>
                  <CardDescription>
                    Compare rule attributes before and after the prompt action.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Name change comparison */}
                  {(result.previous_name || result.name) && result.previous_name !== result.name && (
                    <div className="space-y-2">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Rule Name Change
                      </span>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-3.5 bg-red-50/50 border border-red-200 rounded-lg">
                          <span className="text-[10px] font-bold text-red-600 block mb-1">PREVIOUS NAME</span>
                          <span className="text-sm font-semibold text-red-800 line-through">
                            {result.previous_name || "(None)"}
                          </span>
                        </div>
                        <div className="p-3.5 bg-emerald-50/50 border border-emerald-200 rounded-lg">
                          <span className="text-[10px] font-bold text-emerald-600 block mb-1">UPDATED NAME</span>
                          <span className="text-sm font-semibold text-emerald-800">
                            {result.name || "(None)"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Body change (Script code diff) */}
                  {(result.previous_body || result.body) && result.previous_body !== result.body && (
                    <div className="space-y-2">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Rule Code Script Diff
                      </span>
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {/* Old script code panel */}
                        <div className="border border-red-200 rounded-lg overflow-hidden">
                          <div className="bg-red-50 px-4 py-2 border-b border-red-200 flex justify-between items-center">
                            <span className="text-xs font-semibold text-red-800">Previous Rule Script</span>
                            <Badge variant="outline" className="bg-red-100 text-red-700 hover:bg-red-100 border-red-200 text-[10px]">DELETED</Badge>
                          </div>
                          <pre className="p-4 bg-red-50/20 text-xs font-mono overflow-auto max-h-[350px] leading-relaxed text-red-900 whitespace-pre-wrap">
                            {result.previous_body || "// No previous body"}
                          </pre>
                        </div>

                        {/* New script code panel */}
                        <div className="border border-emerald-200 rounded-lg overflow-hidden">
                          <div className="bg-emerald-50 px-4 py-2 border-b border-emerald-200 flex justify-between items-center">
                            <span className="text-xs font-semibold text-emerald-800">Updated Rule Script</span>
                            <Badge variant="outline" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200 text-[10px]">ADDED</Badge>
                          </div>
                          <pre className="p-4 bg-emerald-50/20 text-xs font-mono overflow-auto max-h-[350px] leading-relaxed text-emerald-900 whitespace-pre-wrap">
                            {result.body || "// No updated body"}
                          </pre>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Rules List Section */}
            {result.rules && result.rules.length > 0 && (
              <Card className="border-gray-200 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Layers className="h-5 w-5 text-workfinity-500" />
                    Retrieved Rules ({result.rules.length})
                  </CardTitle>
                  <CardDescription>
                    Rules matched or affected by the natural language instruction.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-y border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                          <th className="py-3.5 px-6 w-20">ID</th>
                          <th className="py-3.5 px-6">Rule Name</th>
                          <th className="py-3.5 px-6 w-32">Status</th>
                          <th className="py-3.5 px-6 w-40">Modified Date</th>
                          <th className="py-3.5 px-6 w-40">Modified By</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 text-sm text-gray-700">
                        {result.rules.map((rule) => (
                          <React.Fragment key={rule.ID}>
                            <tr className="hover:bg-gray-50/80 transition-colors">
                              <td className="py-4 px-6 font-mono font-semibold text-gray-900">
                                {rule.ID}
                              </td>
                              <td className="py-4 px-6">
                                <div className="font-semibold text-gray-800">{rule.Name}</div>
                                {rule.Description && (
                                  <div className="text-xs text-gray-500 mt-0.5">{rule.Description}</div>
                                )}
                              </td>
                              <td className="py-4 px-6">
                                <Badge className={rule.Status === 1 ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-800"}>
                                  {rule.Status === 1 ? "Active" : "Inactive"}
                                </Badge>
                              </td>
                              <td className="py-4 px-6 text-xs text-gray-500">
                                {rule.ModifiedDate ? (
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {new Date(rule.ModifiedDate).toLocaleDateString()}
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td className="py-4 px-6 text-xs text-gray-500 font-medium">
                                {rule.ModifiedBy || "-"}
                              </td>
                            </tr>

                            {rule.Body && (
                              <tr className="bg-gray-50/30">
                                <td colSpan={5} className="py-3 px-6 border-b border-gray-100">
                                  <div className="bg-gray-800 rounded-lg p-4 text-xs font-mono text-gray-200 overflow-x-auto whitespace-pre-wrap max-h-40">
                                    <div className="text-[10px] font-bold text-gray-400 mb-1 flex items-center gap-1">
                                      <Eye className="h-3.5 w-3.5" />
                                      RULE BODY / LOGIC EXPRESSION
                                    </div>
                                    {rule.Body}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
